export interface SipaPublication {
  id: string;
  title: string;
  doi: string;
  publishedAt: string; // ISO date YYYY-MM-DD
  journal: string;
  // BACKEND NOTE (SIPA-PDF): The backend must populate this field from the SIPA
  // integration when returning publication records. In the prototype it is a mock
  // URL; in production it will point to the actual PDF stored in SIPA/S3.
  pdfUrl?: string;
}

const PUB_BY_INVESTIGATOR: Record<string, SipaPublication[]> = {
  // María González — RUN 11.111.111-1 (normalizado por useIPSync)
  '11.111.111-1': [
    {
      id: 'sipa-001',
      title: 'Diseño de redes neuronales recurrentes para la predicción de series temporales en sistemas de energía renovable',
      doi: '10.1016/j.renene.2024.001234',
      publishedAt: '2024-09-15',
      journal: 'Renewable Energy',
      pdfUrl: '/mock-pdfs/renene-2024-001234.pdf',
    },
    {
      id: 'sipa-002',
      title: 'Optimización multiobjetivo de microrredes eléctricas mediante algoritmos evolutivos distribuidos',
      doi: '10.1109/TPWRS.2024.3456789',
      publishedAt: '2024-03-22',
      journal: 'IEEE Transactions on Power Systems',
      pdfUrl: '/mock-pdfs/tpwrs-2024-3456789.pdf',
    },
    {
      id: 'sipa-003',
      title: 'Transferencia de aprendizaje en modelos de visión computacional aplicados a diagnóstico de falla en turbinas eólicas',
      doi: '10.1016/j.ymssp.2023.110892',
      publishedAt: '2023-11-08',
      journal: 'Mechanical Systems and Signal Processing',
    },
    {
      id: 'sipa-004',
      title: 'Análisis de incertidumbre en la integración de vehículos eléctricos a redes inteligentes: un enfoque probabilístico',
      doi: '10.1016/j.epsr.2023.109445',
      publishedAt: '2023-05-30',
      journal: 'Electric Power Systems Research',
      pdfUrl: '/mock-pdfs/epsr-2023-109445.pdf',
    },
    {
      id: 'sipa-005',
      title: 'Modelado y control predictivo basado en datos para sistemas de almacenamiento de energía en baterías de litio',
      doi: '10.1016/j.apenergy.2022.119801',
      publishedAt: '2022-12-14',
      journal: 'Applied Energy',
    },
    {
      id: 'sipa-006',
      title: 'Marco teórico para la evaluación de flexibilidad en sistemas de potencia con alta penetración de energías renovables',
      doi: '10.1109/ACCESS.2022.3201567',
      publishedAt: '2022-07-03',
      journal: 'IEEE Access',
    },
    {
      id: 'sipa-007',
      title: 'Detección de anomalías en tiempo real en infraestructuras críticas de energía usando aprendizaje federado',
      doi: '10.1016/j.ijepes.2021.107234',
      publishedAt: '2021-10-19',
      journal: 'International Journal of Electrical Power & Energy Systems',
    },
  ],

  // Jorge Fuentes — RUN 11.111.111-6
  '11.111.111-6': [
    {
      id: 'sipa-101',
      title: 'Caracterización mecánica de materiales compuestos de fibra de carbono fabricados mediante impresión 3D de deposición fundida',
      doi: '10.1016/j.compositesb.2024.111592',
      publishedAt: '2024-08-20',
      journal: 'Composites Part B: Engineering',
    },
    {
      id: 'sipa-102',
      title: 'Integridad estructural de uniones adhesivas en estructuras aeronáuticas sometidas a cargas de fatiga de espectro variable',
      doi: '10.1016/j.ijfatigue.2024.108123',
      publishedAt: '2024-02-05',
      journal: 'International Journal of Fatigue',
    },
    {
      id: 'sipa-103',
      title: 'Propagación de grietas por fatiga en aleaciones de aluminio de alta resistencia: modelado computacional y validación experimental',
      doi: '10.1016/j.engfracmech.2023.109567',
      publishedAt: '2023-09-11',
      journal: 'Engineering Fracture Mechanics',
    },
    {
      id: 'sipa-104',
      title: 'Simulación de elementos finitos del comportamiento de laminados CFRP bajo impacto de baja velocidad',
      doi: '10.1016/j.compstruct.2022.115890',
      publishedAt: '2022-04-28',
      journal: 'Composite Structures',
    },
    {
      id: 'sipa-105',
      title: 'Evaluación no destructiva de daño por impacto en materiales compuestos aeroespaciales mediante tomografía computarizada de rayos X',
      doi: '10.1016/j.ndteint.2021.102398',
      publishedAt: '2021-06-17',
      journal: 'NDT & E International',
    },
    {
      id: 'sipa-106',
      title: 'Diseño óptimo de paneles sándwich con núcleo de celosía para aplicaciones de ingeniería ligera',
      doi: '10.1016/j.tws.2020.107124',
      publishedAt: '2020-11-30',
      journal: 'Thin-Walled Structures',
    },
  ],

  // Carolina Rivas — RUN 11.111.111-7
  '11.111.111-7': [
    {
      id: 'sipa-201',
      title: 'Brechas de acceso digital en territorios rurales de Chile: infraestructura, competencias y usos diferenciados',
      doi: '10.5354/0719-1529.2024.72341',
      publishedAt: '2024-06-10',
      journal: 'Revista de Sociología, Universidad de Chile',
    },
    {
      id: 'sipa-202',
      title: 'Inclusión digital en comunidades indígenas mapuche: tensiones entre conectividad y soberanía cultural',
      doi: '10.4067/S0718-07052024000100089',
      publishedAt: '2024-01-15',
      journal: 'Estudios Pedagógicos',
    },
    {
      id: 'sipa-203',
      title: 'Políticas públicas de alfabetización digital en Chile (2015–2023): un análisis crítico de programas y alcance territorial',
      doi: '10.7764/cuhso.2023.34.1.45',
      publishedAt: '2023-08-22',
      journal: 'CUHSO, Universidad Católica de Temuco',
    },
    {
      id: 'sipa-204',
      title: 'El rol de las bibliotecas públicas en la reducción de la brecha digital en zonas periurbanas de la región metropolitana',
      doi: '10.5354/rcb.2022.67890',
      publishedAt: '2022-12-05',
      journal: 'Revista Chilena de Bibliotecología',
    },
    {
      id: 'sipa-205',
      title: 'Género y uso de tecnologías digitales en adultos mayores: patrones de adopción y barreras persistentes en Chile',
      doi: '10.4067/S0718-22362022000200123',
      publishedAt: '2022-06-18',
      journal: 'Última Década',
    },
    {
      id: 'sipa-206',
      title: 'Teleducación y desigualdad en la pandemia: análisis longitudinal de resultados de aprendizaje en establecimientos municipales',
      doi: '10.7764/pensamientoeducativo.2021.1.3',
      publishedAt: '2021-09-30',
      journal: 'Pensamiento Educativo',
    },
  ],
};

export function getSipaPublications(investigatorId: string): SipaPublication[] {
  return PUB_BY_INVESTIGATOR[investigatorId] ?? [];
}
